import React, { useState, useCallback, lazy, Suspense, useRef } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Material
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';

// ReactCSV
import { CSVLink } from 'react-csv';

// Helpers
import { formatDateAndTime } from 'helpers/date';

// Actions
import { travelByLineReportRequest } from 'store/ducks/reports/actions';
import { setOpen, setClose } from 'store/ducks/dialogs/actions';

// Types
import { ApplicationState } from 'store';
import { TravelByLineInterface } from 'store/ducks/reports/types';

// Components
import {
  MainContent,
  DialogueExport,
  SearchComponent,
  XlsHeaderInterface,
  XlsRowInterface,
  ReportTitle,
} from 'components';
import { TablePlaceholder } from 'components/TablePlaceholder';
import { TabPanel } from 'components/TabPanel';

// Constants
import { REPORTS_TYPES } from 'constants/report';
import ITEMS from 'constants/itemsByFilter';
import TITLES_OPTIONS from 'constants/infoModulesTitle';
import { Submissions, History } from '../../../../components';

// Styles
import useStyles from './TravelByLineDatails.styles';

// Lazy Components
const TableTravelByLineReport = lazy(() =>
  import('./components/TableTravelByLineReport/TableTravelByLineReport'),
);
const Filter = lazy(() => import('components/Filter'));
const TotalCountComponent = lazy(() =>
  import('components/TotalCountComponent'),
);

interface TravelByLineDatailsProps {
  setSubmissionData: (param: any) => void;
  setIsAddSubmissiion: (param: any) => void;
  setIsEditSubmissiion: (param: any) => void;
}

const TravelByLineDatails: React.FC<TravelByLineDatailsProps> = ({
  setSubmissionData,
  setIsAddSubmissiion,
  setIsEditSubmissiion,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const csvRef = useRef(null);

  const [xlsHeader, setXlsHeader] = useState<XlsHeaderInterface[]>();
  const [xlsRows, setXlsRows] = useState<Array<XlsRowInterface[]>>([]);
  const [generalNavGuide, setGeneralNavGuide] = useState(0);
  const [tableReference, setTableReference] = useState<unknown>();
  const [reportIsVisible, setReportIsVisible] = useState(false);
  const [tableCSV, setTableCSV] = useState('');
  const [travelByLineList, setTravelByLineList] = useState<
    TravelByLineInterface[]
  >();
  const [periodString, setPeriodString] = useState('');
  const [simplePdfData, setSimplePdfData] = useState<{
    title: string;
    subtitle: string;
    filters: string;
    headers: any;
    rows: any;
  }>({
    title: '',
    subtitle: '',
    filters: '',
    headers: [],
    rows: [],
  });

  const travelByLineReportList = useSelector(
    (state: ApplicationState) => state.reports.travelByLineReportList,
  );

  const dialog = useSelector((state: ApplicationState) => state.dialogs.dialog);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setGeneralNavGuide(newValue);
  };

  const handleLoadData = useCallback(() => {
    setReportIsVisible(true);
    dispatch(
      travelByLineReportRequest({
        startDate: new Date().setHours(0, 0, 0),
        endDate: new Date().setHours(23, 59, 59),
      }),
    );
    const tempPeriodString = `Dia: ${new Date().toLocaleDateString('pt-BR', {
      dateStyle: 'short',
    })}`;
    setPeriodString(tempPeriodString);
  }, []);

  const handleApply = useCallback((data: any) => {
    const {
      startedAt,
      startDate,
      endDate,
      selectedLines,
      selectedSeccionedLine,
      selectedAddIdleKm,
    } = data;

    setReportIsVisible(true);
    dispatch(setClose({}));

    const isPeriod = startDate?.getTime() < endDate?.getTime();
    let startDateValue;
    let endDateValue;
    if (isPeriod) {
      startDateValue = new Date(startDate).getTime();
      endDateValue = new Date(endDate).getTime();
    } else if (startedAt) {
      startDateValue = new Date(startedAt).setHours(0, 0, 0);
      endDateValue = new Date(startedAt).setHours(23, 59, 59);
    }
    dispatch(
      travelByLineReportRequest({
        startDate: startDateValue,
        endDate: endDateValue,
        lineId:
          selectedLines?.length > 0
            ? selectedLines.map((e: any) => e.id).join()
            : undefined,
        seccioned:
          selectedSeccionedLine !== null
            ? Boolean(selectedSeccionedLine)
            : undefined,
        addIdleKm: selectedAddIdleKm === true ? true : undefined,
      }),
    );

    const tempPeriodString = `De: ${
      isPeriod
        ? formatDateAndTime(new Date(startDate))
        : formatDateAndTime(new Date(startedAt))
    } até: ${
      isPeriod
        ? formatDateAndTime(new Date(endDate))
        : formatDateAndTime(new Date(startedAt))
    }`;

    let updatedPeriodString = tempPeriodString;

    if (selectedLines?.length > 0) {
      const tempLinesString = `Linhas: ${selectedLines
        .map((e: any) => e.name)
        .join(', ')}`;
      updatedPeriodString += ', ' + tempLinesString;
    }

    if (selectedSeccionedLine !== null) {
      const tempLinesString = `Filtro por Trecho: ${Boolean(
        selectedSeccionedLine,
      )}`;
      updatedPeriodString += ', ' + tempLinesString;
    }
    setPeriodString(updatedPeriodString);
  }, []);

  return (
    <div className={classes.root}>
      <MainContent className={classes.paperRoot}>
        <Grid
          container
          justifyContent='space-between'
          className={classes.gridContainer}>
          <Grid container className={classes.paperTitle}>
            <ReportTitle
              titleVariant='h4'
              title='Relatórios de Operação • Viagens por Linha'
              tooltipTitle={
                TITLES_OPTIONS.REPORTS.OPERATION.TRAVEL_BY_LINE.VALUE
              }
            />
            <Grid item>
              <CSVLink
                filename='Relatório de Viagens por Linha.csv'
                data={tableCSV}
                ref={csvRef}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid container direction='column'>
          <Grid item className={classes.scopeFilter}>
            <Box
              display='flex'
              alignItems='center'
              width='100%'
              marginTop='16px'>
              <SearchComponent
                disabledInput={!reportIsVisible}
                data={travelByLineReportList}
                itemsByFilter={ITEMS.ReportTravelByLine}
                results={(data: any[]) => {
                  setTravelByLineList(data);
                }}
                modalFilter={() => {
                  dispatch(
                    setOpen({
                      open: true,
                      content: (
                        <Suspense fallback={<p>Carregando...</p>}>
                          <Filter
                            fields={ITEMS.travelByLine.remote}
                            handleApply={handleApply}
                          />
                        </Suspense>
                      ),
                      maxWidth: 'xl',
                    }),
                  );
                }}
              />
              {/* <Grid container justifyContent='flex-end'>
                <Button
                  className={classes.addButton}
                  onClick={() => setIsAddSubmissiion(true)}>
                  <Typography style={{ marginRight: 8, fontSize: 20 }}>
                    +
                  </Typography>{' '}
                  <Typography style={{ fontSize: 12 }}>
                    Programar Novo
                  </Typography>
                </Button>
              </Grid> */}
            </Box>
          </Grid>
        </Grid>
        <Tabs
          indicatorColor='primary'
          onChange={handleChange}
          style={{
            background: '#ffffff',
            margin: '10px 0px 32px 16px',
          }}
          textColor='primary'
          value={generalNavGuide}>
          <Tab label='Relatório' />
          <Tab label='Envios' />
          <Tab label='Histórico' />
        </Tabs>

        <TabPanel index={0} value={generalNavGuide}>
          {!reportIsVisible && <TablePlaceholder onClick={handleLoadData} />}
          {reportIsVisible && (
            <>
              <Grid container justifyContent='flex-end' alignItems='center'>
                {reportIsVisible && travelByLineReportList?.length > 0 && (
                  <Box width='100%' display='flex' justifyContent='flex-end'>
                    <Suspense fallback='Carregando...'>
                      <TotalCountComponent
                        count={travelByLineReportList?.length || 0}
                        title='linhas'
                        position='right'
                      />
                    </Suspense>
                  </Box>
                )}

                <Box paddingRight='16px'>
                  <DialogueExport
                    isDialogPrint
                    tableInstance={tableReference}
                    csvRef={csvRef}
                    pdfName='tabela relatorio de viagens por linha'
                    simplePdf
                    columnsPerPage={3}
                    simplePdfData={simplePdfData}
                    xlsTitle='Relatório de viagens por linha'
                    xlsHeader={xlsHeader}
                    xlsRows={xlsRows}
                    pdf
                    csv
                    xls
                  />
                </Box>
              </Grid>
              <Suspense fallback={<p>Carregando...</p>}>
                <Grid ref={(node: unknown) => setTableReference(node)}>
                  {dialog.open && dialog.isDialogPrint && (
                    <Typography color='inherit' variant='h4'>
                      Relatórios de Operação • Viagens por Linha
                    </Typography>
                  )}
                  <TableTravelByLineReport
                    setTableCSV={setTableCSV}
                    travelByLineList={travelByLineList}
                    periodString={periodString}
                    setSimplePdfData={setSimplePdfData}
                    setXlsHeader={setXlsHeader}
                    setXlsRows={setXlsRows}
                  />
                </Grid>
              </Suspense>
            </>
          )}
        </TabPanel>
        <TabPanel index={1} value={generalNavGuide}>
          <Submissions
            setSubmissionData={setSubmissionData}
            setIsEditSubmissiion={setIsEditSubmissiion}
                setIsAddSubmissiion={setIsAddSubmissiion}
            reportType={REPORTS_TYPES.LINE_KM}
          />
        </TabPanel>
        <TabPanel index={2} value={generalNavGuide}>
          <History reportType={REPORTS_TYPES.LINE_KM} />
        </TabPanel>
      </MainContent>
    </div>
  );
};

export { TravelByLineDatails };
export default TravelByLineDatails;
